// The desktop right-hand detail panel. Extracted from PurchasingQueuePage.js at
// Session 79.14c.
//
// ITS MOBILE TWIN IS MobileDetailSheet AND THE TWO MUST BE CHANGED TOGETHER.
// They render the same facts in two layouts, which is the divergence #12, #13
// and #215 each cost a session to collapse. #545's identifier block and #304's
// settled notice both land on BOTH surfaces or neither.

import React from 'react';
import { IconPackage, IconTruck } from '@tabler/icons-react';
import { fmtDate } from '../../lib/utils';
import { ageDays, requestedSummary, BRANCHES } from '../../lib/purchasingQueue';
import { itemLabel, itemUom, trackingSummary, trackingLabel, preferredVendorPicks } from '../../lib/requestItems';
import { INVENTORY_BUCKET } from '../../lib/storedFile';
import { canCancelFromHere } from '../../lib/materialRequests';
import StoredImage from '../StoredImage';
import VendorContactCard from '../inventory/VendorContactCard';
import TrackingRow from '../inventory/TrackingRow';
import PurchaseOrderDownload from './PurchaseOrderDownload';
import AgeBadge from './AgeBadge';
import ApprovedToOrderBanner from './ApprovedToOrderBanner';
import VendorOrderNumberRow from './VendorOrderNumberRow';
import OrderIdentifiers from '../OrderIdentifiers';

export default function DetailPanel({ req, leadKey = null, openMarkOrdered, openTracking, openApproval, onCancelRequest, canContact, canAction, canApprovePurchase, canShortClose, onShortClose, onReopen, onSaveVendorOrderRef }) {
  if (!req) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9A9A9A', gap: 8 }}>
      <IconPackage size={32} color="#D0D0D0" />
      <div style={{ fontSize: 13 }}>Select a request to view details</div>
    </div>
  );

  const items = req.material_request_items || [];
  const item = items[0];
  const isOffCatalog = item?.is_off_catalog;
  const itemName = isOffCatalog ? (item.off_catalog_description || '—') : (item?.inventory_items?.name || '—');
  const isMultiItem = items.length > 1;
  const track = trackingSummary(items);
  const qty = item?.qty_requested ?? '—';
  const uom = itemUom(item);
  const branchName = BRANCHES.find(b => b.id === req.branch_id)?.name || '—';
  // #304. A settled order — fully received, or received AND fully billed — is
  // what drops off this queue by #284's design. Both statuses, because an order
  // can settle for receiving before its last invoice arrives.
  const isSettled = req.status === 'received' || req.status === 'closed';
  const isPending = req.status === 'approved';
  const isNeedsAction = isPending;
  const vendorPicks = preferredVendorPicks(items);
  const preferred = isMultiItem ? null : (item?.inventory_items?.vendor_items?.find(vi => vi.is_preferred) || null);
  const preferredVendor = preferred?.inventory_vendors || null;

  return (
    <div style={{ padding: '16px 20px', overflowY: 'auto', height: '100%' }}>
      {/* #545, D13. This line was the bare request number and nothing else, so
          the one panel a buyer opens to answer "which order is this?" answered
          it with the single identifier she is least likely to be holding. The
          vendor rings quoting their own reference or the PO Stede issued; both
          were unreachable from this screen. All six now, dashes included. */}
      <OrderIdentifiers req={req} leadKey={leadKey} style={{ marginBottom: 6 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0F0F0F' }}>
          {itemName}
          {isMultiItem && <span style={{ fontWeight: 400, fontSize: 15, color: '#5C5C5C' }}> +{items.length - 1} more</span>}
        </div>
        {isOffCatalog && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#A32D2D', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 6, padding: '2px 8px' }}>
            Not in catalog
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>
        {branchName} branch · {items.length} item{items.length !== 1 ? 's' : ''} · <AgeBadge ts={req.created_at} />
      </div>

      <ApprovedToOrderBanner req={req} />

      {/* #410. Renders only for an order that has actually been placed — before
          that there is no vendor to have confirmed anything. */}
      <VendorOrderNumberRow req={req} canAction={canAction} onSave={onSaveVendorOrderRef} />

      {/* #413, Session 78n8.3. The buyer's own copy of what she sent.
          Until now the Mark as ordered sheet was the ONLY screen in Stede that
          could build a purchase order, and closing it destroyed the only copy —
          so "can you resend me that PO?" had no answer even for the person who
          sent it twenty minutes earlier. */}
      {req.status === 'ordered' && (
        <div style={{ marginBottom: 12 }}>
          <PurchaseOrderDownload poDocument={req.po_document} label="Download the PO sent" />
        </div>
      )}

      {isOffCatalog && item?.photo_url && (
        <StoredImage value={item.photo_url} bucket={INVENTORY_BUCKET} alt="Request photo" style={{ width: '100%', borderRadius: 8, marginBottom: 12, objectFit: 'cover', maxHeight: 160 }} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>Requested by</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{req.users?.full_name || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>{isMultiItem ? 'Items' : 'Qty needed'}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{isMultiItem ? `${items.length} items` : `${qty} ${uom}`}</div>
        </div>
        {/* The Work order cell used to sit here and render only when the request
            carried one, so this grid silently changed shape between two orders.
            It is in the identifier block at the top of the panel now, where it
            keeps its label whether or not there is a number — D15. */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>Waiting</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: ageDays(req.created_at) >= 3 ? '#9A5F00' : '#0F0F0F' }}>{ageDays(req.created_at)} days</div>
        </div>
        {isOffCatalog && item?.needed_by && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>Needed by</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{new Date(item.needed_by + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
          </div>
        )}
      </div>

      {isOffCatalog && (
        <div style={{ background: '#FFF8EC', border: '1px solid #EF9F27', borderLeft: '3px solid #EF9F27', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9A5F00', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Description</div>
          <div style={{ fontSize: 13, color: '#3C3C3C', lineHeight: 1.5 }}>{item.off_catalog_description}</div>
          {item.off_catalog_vendor_name && (
            <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 6 }}>Suggested vendor: <strong>{item.off_catalog_vendor_name}</strong></div>
          )}
        </div>
      )}

      {req.notes && (
        <div style={{ background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#633806', marginBottom: 12 }}>
          {req.notes}
        </div>
      )}

      {!isOffCatalog && isMultiItem && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Preferred vendors</div>
          {vendorPicks.length > 0 ? vendorPicks.map(p => (
            <div key={p.vendor.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '5px 0', borderBottom: '1px solid #F5F5F3' }}>
              <span style={{ fontWeight: 600, color: '#0F0F0F' }}>{p.vendor.name}</span>
              <span style={{ color: '#7A7A7A', flexShrink: 0 }}>{p.count} of {items.length} items</span>
            </div>
          )) : (
            <div style={{ fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>No preferred vendor on file for any item</div>
          )}
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 6, marginBottom: 8 }}>
            Per-item pricing and vendor contact are on the Mark ordered sheet.
          </div>
        </>
      )}

      {!isOffCatalog && !isMultiItem && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Preferred vendor</div>
          {preferredVendor && (
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>{preferredVendor.name}</div>
          )}
          <div style={{ fontSize: 11, color: '#5C5C5C', marginBottom: 8 }}>
            {preferred?.vendor_item_number
              ? <>Item # <strong style={{ color: '#0F0F0F' }}>{preferred.vendor_item_number}</strong>{preferred?.unit_cost != null ? ` · Last price $${parseFloat(preferred.unit_cost).toFixed(2)}/${uom || 'unit'}` : ''}</>
              : <span style={{ color: '#B0B0B0', fontStyle: 'italic' }}>No item # on file{preferred?.unit_cost != null ? ` · Last price $${parseFloat(preferred.unit_cost).toFixed(2)}/${uom || 'unit'}` : ''}</span>
            }
          </div>
          {canContact && <VendorContactCard vendor={preferredVendor} />}
        </>
      )}

      {isMultiItem && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Items on this request</div>
          {items.map(it => (
            <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: '#3C3C3C', padding: '5px 0', borderBottom: '1px solid #F5F5F3' }}>
              <span style={{ flex: 1, minWidth: 0 }}>{itemLabel(it)}</span>
              <span style={{ flexShrink: 0, color: '#7A7A7A' }}>x{it.qty_requested} {itemUom(it)}</span>
            </div>
          ))}
        </div>
      )}

      {/* #304, Session 79.14d. THIS CONDITION USED TO READ `status === 'ordered'`
          ALONE, and that is the second half of #304 rather than a detail. The
          reopen control is the only way to reverse a short close from the
          buyer's own screen, it lives inside this block, and a settled order
          is `received` or `closed` - so #304's search would have delivered
          her to a panel showing the order and carrying NO BUTTONS AT ALL.
          The gate's D8 asserted the opposite and is corrected.

          `canAction` goes false once settled: TrackingRow already hides Add
          tracking on a closed LINE, but a settled order can hold lines that
          simply arrived, and offering to add a tracking number to an order
          nobody is chasing is the thing On order exists not to do. */}
      {(req.status === 'ordered' || isSettled) && items.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px' }}>{isSettled ? 'Lines on this order' : 'Tracking'}</div>
            <div style={{ fontSize: 11, color: '#7A7A7A' }}>{isSettled ? 'Settled' : trackingLabel(track)}</div>
          </div>
          {items.map(it => (
            <TrackingRow
              key={it.id}
              it={it}
              canAction={canAction && !isSettled}
              onAddTracking={target => openTracking(req, target)}
              canShortClose={canShortClose}
              onShortClose={target => onShortClose(req, target)}
              onReopen={onReopen}
            />
          ))}
          {/* Gate callout J. One sentence, and it exists to prevent a second
              surprise: reopening a line silently returns the order to a list
              the buyer is not currently looking at. Said beforehand, the order
              reappearing on On order is the expected outcome rather than the
              next thing she has to work out. Rendered only when there is
              something here that can actually be reopened. */}
          {isSettled && canShortClose && items.some(it => it.receipt_closed) && (
            <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 8, lineHeight: 1.45 }}>
              Reopening a line puts this order back on <strong style={{ color: '#5C5C5C' }}>On order</strong>.
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Request history</div>
        <div style={{ fontSize: 11, color: '#7A7A7A' }}>
          {fmtDate(req.created_at)} · {req.users?.full_name || '—'} requested {requestedSummary(items)}
          {req.work_order_ref ? ` · ${req.work_order_ref}` : ''}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {isNeedsAction && canAction && (
          <button
            onClick={() => openMarkOrdered(req)}
            style={{ width: '100%', padding: '10px 0', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Mark ordered
          </button>
        )}
        {req.status === 'ordered' && canAction && items.length === 1 && (
          <button
            onClick={() => openTracking(req, items[0])}
            style={{
              width: '100%', padding: '10px 0',
              background: track.tracked > 0 ? 'white' : '#0F0F0F',
              color: track.tracked > 0 ? '#0F0F0F' : 'white',
              border: track.tracked > 0 ? '1px solid #E0E0E0' : 'none',
              borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <IconTruck size={14} />
            {track.tracked > 0 ? 'Update tracking' : 'Add tracking'}
          </button>
        )}
        {/* #642 -- every status the database lets Purchasing cancel from, not
            `isPending` alone: an order already placed is the case where
            canceling matters most. `isPending` is left exactly as it is. */}
        {canCancelFromHere(req) && canAction && (
          <button
            onClick={() => onCancelRequest(req)}
            style={{ width: '100%', padding: '10px 0', background: 'white', color: '#A32D2D', border: '1px solid #F09595', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Cancel request
          </button>
        )}
        {req.status === 'pending_purchase_approval' && canApprovePurchase && (
          <button
            onClick={() => openApproval(req)}
            style={{ width: '100%', padding: '10px 0', background: '#993556', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Review approval
          </button>
        )}
      </div>
    </div>
  );
}
